import { dealsApi } from "@/api/deals-api";
import { useUser } from "@clerk/clerk-react";
import { isNarrowedBrowse, useDealsListStore } from "@/store/deals-list-store";
import { TOUR_TYPES, useTourStore } from "@/store/tour-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useDealsViewStore } from "@/store/deals-view-store";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { Skeleton } from "../ui/skeleton";
import { IndeterminateBar } from "../ui/indeterminate-bar";
import { Button } from "../ui/button";
import { DealsEmptyOnboarding } from "./empty-onboarding";
import { FilterNode, serializeFilters } from "./filters";
import { isServerSortable } from "./fields";
import {
  getStatusCounts,
  dealViewsApi,
  type DealView,
  type AiColumn,
} from "@/api/deals-api";
import { ViewTabs } from "./view-tabs";
import { deserializeFilters } from "./filters";
import { useQuery } from "@tanstack/react-query";
import { MobileView } from "./mobile-view";
import { TableView } from "./table-view";
import { KanbanView } from "./kanban-view";
import { LookbookVisibilityHost } from "./lookbook-visibility-host";
import { Toolbar } from "./toolbar";
import { StageFunnelStrip } from "./stage-funnel-strip";
import type { AiCellValue } from "./ai-column-cell";
import { isAiCellRunning } from "./ai-cell-state";
import { getLegacyPendingIndustryEntries } from "@/lib/deal-field-state";
import { useDealsListReportStatusSocket } from "@/hooks/use-deals-list-report-status-socket";

// useMediaQuery — listen for viewport changes (replaces the buggy
// `window.innerWidth` snapshot at render that didn't update on resize).
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

/** Matches the server's DEFAULT_PAGE_SIZE. */
const PAGE_SIZE = 25;
/** Matches the report-status poll fallback, for the same reason. */
const AI_COLUMN_POLL_INTERVAL_MS = 5000;

export const DealsComponent = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [dealStatuses, setDealStatuses] = useState<Record<string, string>>({});
  const { deals, isLoading, hasLoaded, fetchDeals, refreshDeals, updateDeal } =
    useDealsListStore();
  const { activeWorkspaceId, activeWorkspace, fetchWorkspaces } =
    useWorkspaceStore();
  const { view, setSort, setVisibleColumns, visibleColumns } =
    useDealsViewStore();
  const { user } = useUser();
  const [filters, setFilters] = useState<FilterNode[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [isSavingViews, setIsSavingViews] = useState(false);
  const [page, setPage] = useState(1);
  const { sort, pagination } = {
    sort: useDealsViewStore((state) => state.sort),
    pagination: useDealsListStore((state) => state.pagination),
  };
  const loadedBrowse = useDealsListStore((state) => state.loadedBrowse);
  const { startTour } = useTourStore();
  const isMobileView = useMediaQuery("(max-width: 767px)");
  const [searchParams, setSearchParams] = useSearchParams();

  // Consume `?status=<stage>` whenever present and the workspace is loaded.
  // Validates against the workspace's own statuses so a stale or cross-workspace
  // link surfaces a toast instead of silently filtering to an empty board.
  useEffect(() => {
    if (!activeWorkspace) return;
    const statusParam = searchParams.get("status");
    if (!statusParam) return;

    const validTitles = new Set(
      activeWorkspace.statuses?.map((s) => s.title) ?? []
    );
    if (validTitles.has(statusParam)) {
      setFilters([{ field: "status", op: "in", value: [statusParam] }]);
    } else {
      toast.error(
        `Stage "${statusParam}" doesn't exist in this workspace.`
      );
    }

    const next = new URLSearchParams(searchParams);
    next.delete("status");
    setSearchParams(next, { replace: true });
  }, [activeWorkspace, searchParams, setSearchParams]);

  // Keep every row's report-status indicator live (not just the open deal).
  useDealsListReportStatusSocket(activeWorkspaceId);

  // Start tour when deals are loaded
  useEffect(() => {
    if (Object.keys(deals).length > 0) {
      if (isMobileView) {
        startTour(TOUR_TYPES.MOBILE_DEALS_TABLE);
      } else {
        startTour(TOUR_TYPES.DEALS_TABLE);
      }
    }
  }, [deals, isMobileView, startTour]);

  // Initialize dealStatuses from deals when they load
  useEffect(() => {
    const initialStatuses = Object.entries(deals).reduce(
      (acc, [id, deal]) => ({
        ...acc,
        [id]: deal.overview.status,
      }),
      {}
    );
    setDealStatuses(initialStatuses);
  }, [deals]);

  // Diagnostic: surface deals carrying legacy `industries` / `industry`
  // entries that are pending in BE but unreachable from the FE (the
  // Profile tab only renders `sub_industries`). Logged so we can spot
  // deals that need cleanup upstream — the pipeline badge intentionally
  // ignores these to avoid dead-end "Review industries" clicks.
  useEffect(() => {
    if (Object.keys(deals).length === 0) return;
    const offenders = Object.values(deals)
      .map((deal) => ({
        id: deal.id,
        name: deal.overview.name,
        legacy: getLegacyPendingIndustryEntries(deal),
      }))
      .filter((row) => row.legacy.length > 0);
    if (offenders.length > 0) {
      console.warn(
        `[deals] ${offenders.length} deals have legacy pending industry entries (industries/industry field, unreachable from Profile tab):`,
        offenders,
      );
    }
  }, [deals]);

  const editDealStatusMutation = useMutation<
    unknown,
    Error,
    { dealId: string; status: string },
    { previousDeal: (typeof deals)[string]; previousStatus: string }
  >({
    mutationFn: async ({ dealId, status }) => {
      return dealsApi.changeStatus(
        dealId,
        status,
        user?.id ?? "",
        activeWorkspaceId ?? ""
      );
    },
    onMutate: async ({ dealId, status }) => {
      const existingDeal = deals[dealId];
      if (!existingDeal) throw new Error("Deal not found");
      // Fall back to the deal's own status when dealStatuses hasn't tracked
      // this deal yet (first mutation in session). Otherwise Undo would call
      // mutate with status=undefined and corrupt the server-side value.
      const previousStatus =
        dealStatuses[dealId] ?? existingDeal.overview.status;
      const updatedDeal = {
        ...existingDeal,
        overview: {
          ...existingDeal.overview,
          status: status,
        },
      };
      updateDeal(dealId, updatedDeal);
      setDealStatuses((prev) => ({
        ...prev,
        [dealId]: status,
      }));
      return { previousDeal: existingDeal, previousStatus };
    },
    onError: (_err, { dealId }, context) => {
      if (context) {
        updateDeal(dealId, context.previousDeal);
        setDealStatuses((prev) => ({
          ...prev,
          [dealId]: context.previousStatus,
        }));
      }
      toast.error(getErrorMessage(_err, "Failed to update deal status"));
    },
    onSuccess: (_data, { dealId, status: newStatus }, context) => {
      // If the moved deal would no longer match the active stage filter,
      // auto-clear the filter and surface a toast with Undo so the user
      // doesn't think the deal vanished.
      const statusFilter = filters.find((f) => f.field === "status");
      const filtered = Array.isArray(statusFilter?.value)
        ? (statusFilter.value as string[])
        : [];
      const wouldFallOutOfView =
        filtered.length > 0 && !filtered.includes(newStatus);

      if (wouldFallOutOfView) {
        setFilters((prev) => prev.filter((f) => f.field !== "status"));
        toast.success(`Moved to ${newStatus}`, {
          action: {
            label: "Undo",
            onClick: () => {
              if (context) {
                editDealStatusMutation.mutate({
                  dealId,
                  status: context.previousStatus,
                });
              }
            },
          },
        });
      } else {
        toast.success("Deal status updated successfully");
      }
    },
  });

  const views = ((activeWorkspace as { deal_views?: DealView[] } | undefined)
    ?.deal_views ?? []) as DealView[];

  const aiColumns = ((activeWorkspace as { ai_columns?: AiColumn[] } | undefined)
    ?.ai_columns ?? []) as AiColumn[];

  /**
   * Applying a view means adopting its filters, columns and sort.
   *
   * A list view carries no filters — its membership is sent to the server as
   * `view`, and the server turns that into a filter on the deal. That is the
   * whole reason lists and filters are one concept here.
   */
  const selectView = (viewId: string | null) => {
    setActiveViewId(viewId);
    const view = views.find((v) => v._id === viewId);
    if (!view) {
      setFilters([]);
      return;
    }
    setFilters(deserializeFilters(view.filter ?? []));
    if (view.columns?.length) setVisibleColumns(view.columns);
    if (view.sort?.field) setSort(view.sort);
  };

  /** Reports whether the save landed, so callers don't confirm a failed write. */
  const saveViews = async (next: Partial<DealView>[]): Promise<boolean> => {
    if (!activeWorkspaceId) return false;
    setIsSavingViews(true);
    try {
      await dealViewsApi.save(activeWorkspaceId, next);
      // The workspace document holds the views, so refetch it rather than
      // trusting local state to match what the server normalised.
      await fetchWorkspaces();
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save views"));
      return false;
    } finally {
      setIsSavingViews(false);
    }
  };

  // Multi-toggle: clicking a strip pill toggles that status in/out of the
  // statuses filter. Clicking "All" (null) clears the filter entirely.
  const handleStageFilterClick = (status: string | null) => {
    setFilters((prev) => {
      const others = prev.filter((f) => f.field !== "status");
      if (status === null) return others;

      const current = prev.find((f) => f.field === "status");
      const selected = Array.isArray(current?.value)
        ? (current.value as string[])
        : [];
      const next = selected.includes(status)
        ? selected.filter((s) => s !== status)
        : [...selected, status];

      return next.length > 0
        ? [...others, { field: "status", op: "in" as const, value: next }]
        : others;
    });
  };

  // The status filter drives the stage strip's pressed state.
  const activeStatuses = useMemo(() => {
    const node = filters.find((f) => f.field === "status");
    return Array.isArray(node?.value) ? (node.value as string[]) : [];
  }, [filters]);

  // Debounced so typing does not fire a request per keystroke now that search
  // is a database query rather than an array filter.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Any change to what is being asked for returns to the first page — staying
  // on page 4 of a result set that now has two pages shows an empty table.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters, sort.field, sort.direction, view, activeViewId]);

  const browse = useMemo(() => {
    // Kanban needs every deal to fill its columns, and sorting by a field the
    // database cannot order by (fit score, gap analysis, company name) has to
    // be done over the whole set — sorting one page by it would silently mean
    // "sort these 25". Both cases load unpaginated.
    const canPage = view === "table" && isServerSortable(sort.field);

    return {
      // The table renders rows; it has no use for the AI documents the full
      // payload carries, and they dominate its size.
      lean: true,
      ...(canPage ? { page, limit: PAGE_SIZE } : {}),
      ...(activeViewId ? { view: activeViewId } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(isServerSortable(sort.field)
        ? { sort: sort.field, dir: sort.direction }
        : {}),
      filters: serializeFilters(filters),
    };
  }, [view, page, debouncedSearch, sort.field, sort.direction, filters, activeViewId]);

  // Counted on the server so the strip shows real totals rather than whichever
  // page happens to be loaded — narrowed by the same view, search and filters
  // as the rows, or a tab reads "NEW 22" above the four deals a view leaves.
  // The server ignores the status filter here: the tabs are that filter.
  const countsScope = useMemo(
    () => ({
      ...(browse.view ? { view: browse.view } : {}),
      ...(browse.search ? { search: browse.search } : {}),
      filters: browse.filters,
    }),
    [browse.view, browse.search, browse.filters]
  );
  const { data: statusCounts } = useQuery({
    queryKey: ["deal-status-counts", activeWorkspaceId, countsScope],
    queryFn: () => getStatusCounts(activeWorkspaceId as string, countsScope),
    enabled: !!activeWorkspaceId,
    // Keep the old counts on screen while a new filter's load, rather than
    // blanking the strip to zeros between keystrokes.
    placeholderData: (previous) => previous,
  });

  // Counted server-side across the whole workspace. Deriving these from the
  // loaded deals used to work because every deal was loaded; with a page in
  // hand it would show "New 25" no matter how many there really are.
  const dealCountByStatus = statusCounts ?? {};

  useEffect(() => {
    if (activeWorkspaceId) {
      fetchDeals(activeWorkspaceId, browse);
    }
  }, [activeWorkspaceId, browse, fetchDeals]);

  // An AI column run is a queue on the server; nothing pushes its progress, so
  // the cells would sit on "Working…" until something else happened to refetch.
  // Polls only while a cell is actually running, and stops when none are.
  const hasRunningAiCells = useMemo(
    () =>
      Object.values(deals).some((deal) =>
        Object.values(
          (deal.aiColumnValues ?? {}) as Record<string, AiCellValue>
        ).some((cell) => isAiCellRunning(cell))
      ),
    [deals]
  );

  useEffect(() => {
    if (!hasRunningAiCells || !activeWorkspaceId) return;
    const id = setInterval(
      () => void refreshDeals(activeWorkspaceId, { silent: true }),
      AI_COLUMN_POLL_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [hasRunningAiCells, activeWorkspaceId, refreshDeals]);

  // Filtering, sorting and paging all happen on the server now. The table used
  // to load every deal in the workspace and narrow it here, which is fine at
  // fifteen deals and untenable at thousands — and it was not only the list:
  // the endpoint also assembles every file, review and AI generation for each
  // deal it returns.
  //
  // Deals arrive already narrowed, so the only thing left to apply locally is
  // the optimistic status change, which has not round-tripped yet.
  const visibleDeals = useMemo(
    () =>
      Object.values(deals).map((deal) => ({
        ...deal,
        overview: {
          ...deal.overview,
          status: dealStatuses[deal.id] || deal.overview.status,
        },
      })),
    [deals, dealStatuses]
  );

  // Only the first load blanks the page. A refetch keeps the current rows up —
  // re-mounting the toolbar mid-refresh also cost the search box its focus.
  if (isLoading && !hasLoaded) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        {/* Toolbar skeleton */}
        <div className="flex flex-col gap-2 px-4 py-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 flex-1 rounded-md" />
            <Skeleton className="h-8 w-20 rounded-md" />
            <Skeleton className="h-8 w-24 rounded-md" />
          </div>
        </div>

        {/* Stage funnel strip skeleton */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5 px-3">
              <Skeleton className="h-6 w-8 rounded" />
              <Skeleton className="h-3 w-14 rounded" />
            </div>
          ))}
        </div>

        {/* Table skeleton */}
        <div className="flex-1 overflow-hidden px-2">
          {/* Header */}
          <div className="flex items-center gap-4 border-b border-border py-3 px-4">
            <Skeleton className="h-3 w-4 rounded" />
            <Skeleton className="h-3 w-24 rounded" />
            <Skeleton className="h-3 w-16 rounded" />
            <Skeleton className="h-3 w-16 rounded" />
            <Skeleton className="h-3 w-12 rounded" />
            <Skeleton className="h-3 w-20 rounded" />
            <Skeleton className="h-3 w-16 rounded" />
            <Skeleton className="h-3 w-20 rounded" />
          </div>
          {/* Rows */}
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-4 border-b border-border/40 py-3.5 px-4"
            >
              <Skeleton className="h-4 w-4 rounded" />
              <div className="flex items-center gap-2 w-[30%]">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="space-y-1.5 flex-1">
                  <Skeleton
                    className="h-3.5 rounded"
                    style={{ width: `${50 + Math.random() * 30}%` }}
                  />
                  <Skeleton className="h-2.5 w-3/4 rounded" />
                </div>
              </div>
              <Skeleton className="h-5 w-14 rounded" />
              <Skeleton className="h-5 w-14 rounded" />
              <Skeleton className="h-4 w-10 rounded" />
              <Skeleton className="h-4 w-20 rounded" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-16 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasNoDeals = Object.keys(deals).length === 0;
  // A saved view narrows the result set too — a curated list starts empty by
  // design, and that is not a workspace with no deals in it.
  const isNarrowed =
    !!debouncedSearch || filters.length > 0 || !!activeViewId;
  // What the rows on screen were actually asked for. Clearing a filter flips
  // `isNarrowed` to false on the spot, but the empty result of the *previous*
  // filtered browse is still in the store until the refetch lands — so
  // deciding the empty state from live state alone flashes the brand-new-
  // workspace onboarding screen over a pipeline that is not empty.
  const wasNarrowed = isNarrowedBrowse(loadedBrowse);
  const isEmptyList =
    !debouncedSearch &&
    filters.length === 0 &&
    views.find((v) => v._id === activeViewId)?.mode === "list";
  // A load with rows already up: the first load renders the skeleton instead.
  const isRefreshing = isLoading && hasLoaded;

  // "No deals" used to be unambiguous, because the store held every deal in the
  // workspace. It now holds whatever the server returned, so an empty result is
  // usually "nothing matched your filters" — and showing a brand-new-workspace
  // onboarding screen there tells the user their pipeline is gone.
  if (hasNoDeals && !isNarrowed && !wasNarrowed) {
    return <DealsEmptyOnboarding />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Toolbar
        onSearch={setSearchQuery}
        filters={filters}
        onFiltersChange={setFilters}
        deals={deals}
        workspaceId={activeWorkspaceId ?? ""}
        aiColumns={aiColumns}
        onAiColumnsSaved={async () => {
          await fetchWorkspaces();
          // Silent: the cells carry their own spinners for a run, and the
          // dialog its own state for a save. Greying the whole table out on
          // top of that says the same thing twice, louder.
          if (activeWorkspaceId)
            await fetchDeals(activeWorkspaceId, browse, { silent: true });
        }}
      />

      <ViewTabs
        views={views}
        activeViewId={activeViewId}
        onSelect={selectView}
        onSave={saveViews}
        currentFilters={filters}
        currentColumns={visibleColumns}
        currentSort={sort}
        isSaving={isSavingViews}
      />

      {/* Stage Funnel Strip — hidden in Kanban view because the columns
          ARE the stage filter; showing both is redundant. */}
      {view !== "kanban" &&
        activeWorkspace?.statuses &&
        activeWorkspace.statuses.length > 0 && (
          <StageFunnelStrip
            statuses={activeWorkspace.statuses}
            dealCountByStatus={dealCountByStatus}
            activeStatuses={activeStatuses}
            onStatusClick={handleStageFilterClick}
          />
        )}

      {/* The rows stay on screen while the next set loads, so without this a
          view or filter change looks like nothing happened until the table
          suddenly changes several seconds later. */}
      <IndeterminateBar active={isRefreshing} label="Loading deals" />

      {hasNoDeals && isNarrowed ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
          {isEmptyList ? (
            // A list holds what you put in it, so there is nothing to clear —
            // offering "Clear filters" here would do nothing.
            <p className="text-sm text-muted-foreground">
              Nothing in this list yet. Add deals to it from the table.
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                No deals match{" "}
                {debouncedSearch ? "your search" : "these filters"}.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  setFilters([]);
                  setSearchQuery("");
                }}
              >
                Clear {filters.length > 0 ? "filters" : "search"}
              </Button>
            </>
          )}
        </div>
      ) : (
      <div
        className={cn(
          "flex-1 overflow-hidden transition-opacity",
          // Rows on screen during a refetch are the *previous* result. Dimming
          // them says so, and ignoring clicks stops a row being acted on just
          // as it is replaced by a different one.
          isRefreshing && "pointer-events-none opacity-60"
        )}
      >
        {/* Mobile view for small screens */}
        <div
          className="block md:hidden h-full overflow-y-auto"
          data-tour="mobile-container"
        >
          <MobileView
            deals={visibleDeals}
            onStatusChange={editDealStatusMutation.mutate}
            workspaceId={activeWorkspaceId ?? ""}
            onDealDeleted={() =>
              activeWorkspaceId && fetchDeals(activeWorkspaceId, browse)
            }
            onDealArchived={() =>
              activeWorkspaceId && fetchDeals(activeWorkspaceId, browse)
            }
            aiColumns={aiColumns}
            onAiColumnRun={() =>
              activeWorkspaceId &&
              fetchDeals(activeWorkspaceId, browse, { silent: true })
            }
          />
        </div>

        {/* Desktop view: table or kanban based on view-switcher */}
        <div className="hidden md:block h-full">
          {view === "kanban" ? (
            <KanbanView
              deals={visibleDeals}
              onStatusChange={editDealStatusMutation.mutate}
              workspaceId={activeWorkspaceId ?? ""}
              onDealDeleted={() =>
                activeWorkspaceId && fetchDeals(activeWorkspaceId, browse)
              }
              onDealArchived={() =>
                activeWorkspaceId && fetchDeals(activeWorkspaceId, browse)
              }
            />
          ) : (
            <div className="flex h-full flex-col">
              <TableView
                deals={visibleDeals}
                aiColumns={aiColumns}
                onAiColumnRun={() =>
                  activeWorkspaceId &&
                  fetchDeals(activeWorkspaceId, browse, { silent: true })
                }
                onStatusChange={editDealStatusMutation.mutate}
                workspaceId={activeWorkspaceId ?? ""}
                onDealDeleted={() =>
                  activeWorkspaceId && fetchDeals(activeWorkspaceId, browse)
                }
                onDealArchived={() =>
                  activeWorkspaceId && fetchDeals(activeWorkspaceId, browse)
                }
              />

              {/* Only when the server actually paged. Sorting by a field it
                  cannot order by loads everything, and showing "Page 1 of 1"
                  there would imply a limit that isn't being applied. */}
              {pagination && pagination.total_pages > 1 && (
                <div className="flex shrink-0 items-center justify-between border-t border-border px-6 py-2 text-xs text-muted-foreground">
                  <span>
                    {(pagination.page - 1) * pagination.limit + 1}–
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}{" "}
                    of {pagination.total}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={pagination.page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span className="px-2">
                      {pagination.page} / {pagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={pagination.page >= pagination.total_pages}
                      onClick={() =>
                        setPage((p) =>
                          Math.min(pagination.total_pages, p + 1)
                        )
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Mounted here, beside the views rather than inside them: the
          add-to-board menus that open this dialog live in Radix dropdowns,
          which unmount their contents on close. */}
      <LookbookVisibilityHost />
    </div>
  );
};
